/**
 * AI Query Hooks Pattern:
 * All AI query hooks automatically check ff-is-ai-enabled before making requests.
 * This prevents 401 errors and unnecessary API calls when AI features are disabled.
 *
 * The pattern uses AND logic with user-provided enabled conditions, so:
 * - If ff-is-ai-enabled is false, query never runs
 * - If ff-is-ai-enabled is true, user's enabled condition applies
 *
 * Note: Mutations (useUpdateAiSettings, setAiSettings) don't need this pattern
 * since they only fire on explicit user actions.
 */
import { UseQueryOptions, UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { IAIAssistantsSettings } from "@performyard/models";
import type {
	AiQueryRequest,
	AiQueryRequestFrontend,
	AiQueryRequestUrlParams,
	GetCompanyAISettingsResponse,
	PatchCompanyAISettingsRequest,
	UserErrorResponseJson,
} from "@performyard/service-contracts";

import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import { companyAISettingsQuery } from "../../queries";
import { patchCompanyAISettings, postCompanyAISettings, postQueryAi } from "../../services/defs/internal-api/ai";

type AiQueryRequestProps = AiQueryRequestFrontend &
	AiQueryRequestUrlParams & {
		recreateCount: number;
		enabled?: boolean;
	};

export const useAiQuery = <TResponse>(props: AiQueryRequestProps) => {
	const { type, recreateCount, enabled = true, ...restProps } = props;
	const { value: ffAiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);

	const baseKey = ["ai-query", type, JSON.stringify(restProps)];

	const queryKey = recreateCount > 0 ? [...baseKey, "retry", recreateCount] : baseKey;

	const isEngagementInsight = type === "engagement-insight";
	const isEngagementCohortInsight = type === "engagement-cohort-insights";

	return useQuery<TResponse | UserErrorResponseJson>({
		queryKey: [...queryKey, JSON.stringify(props)],
		queryFn: () => postQueryAi<TResponse>(props as AiQueryRequest & AiQueryRequestUrlParams),
		staleTime: isEngagementInsight || isEngagementCohortInsight ? Infinity : 0,
		refetchOnWindowFocus: false,
		select: (data) => data,
		enabled: ffAiEnabled && enabled,
	});
};

export const useAiSettings = (
	options?: Partial<UseQueryOptions<GetCompanyAISettingsResponse, Error>>
): UseQueryResult<GetCompanyAISettingsResponse, Error> => {
	const { value: ffAiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);

	return useQuery<GetCompanyAISettingsResponse, Error>({
		...companyAISettingsQuery(),
		...options,
		enabled: ffAiEnabled && (options?.enabled ?? true),
	});
};

export const setAiSettings = () => {
	return useMutation({
		mutationKey: ["ai", "settings"],
		mutationFn: async (aiSettings: IAIAssistantsSettings) => {
			return postCompanyAISettings(aiSettings);
		},
	});
};

export const useUpdateAiSettings = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["ai", "settings"],
		mutationFn: async (aiSettings: PatchCompanyAISettingsRequest) => {
			return patchCompanyAISettings(aiSettings);
		},
		onMutate: async (variables) => {
			await queryClient.cancelQueries({ queryKey: ["ai", "settings"] });
			const previousSettings = queryClient.getQueryData(["ai", "settings"]);

			// Optimistically update the query cache with the new values
			queryClient.setQueryData(["ai", "settings"], (old: GetCompanyAISettingsResponse | undefined) => {
				if (!old) return old;
				return { ...old, ...variables };
			});

			return { previousSettings };
		},
		onSuccess: () => {
			// Invalidate the query in the background to ensure consistency (non-blocking)
			queryClient.invalidateQueries({ queryKey: ["ai", "settings"] });
		},
		onError: (_error, _variables, context) => {
			if (context?.previousSettings) {
				queryClient.setQueryData(["ai", "settings"], context.previousSettings);
			}
		},
	});
};
